import {
  Dispatch,
  SetStateAction,
  useMemo,
  useOptimistic,
  useState,
} from 'react';
import { toast } from 'sonner';

import { ToggleSwitch } from '@/components/legacy-ui';
import { PraetorianSettingsContent } from '@/components/PraetorianSettingsContent';
import { Tooltip } from '@/components/Tooltip';
import { Button } from '@/components/ui/button';
import { useMy } from '@/hooks';
import { useUpdateFlags } from '@/hooks/useFlags';
import { ChariotModuleFlags, FlagType } from '@/types';
import { cn } from '@/utils/classname';

const noop = () => {};

const chariotModuleOptions: {
  name: string;
  description: string;
  value: FlagType;
  positiveFlag?: boolean;
  dependsOn?: FlagType;
  dependencyLabel?: string;
  dependsOnPositiveFlag?: boolean;
  comingSoon?: boolean;
}[] = [
  {
    name: 'Static IP Addresses',
    description: 'Enables scanning from a static IP pool',
    value: FlagType.DISABLE_STATIC_IP_ADDRESSES,
  },
  {
    name: 'AI Inference',
    description: "Enables AI Assistant's query mode",
    value: FlagType.DISABLE_AI_INFERENCE,
  },
  {
    name: 'AI Agents',
    description: "Enables AI Assistant's agent mode",
    value: FlagType.ENABLE_AI_AGENTS,
    positiveFlag: true,
    dependsOn: FlagType.DISABLE_AI_INFERENCE,
    dependencyLabel: 'AI Inference',
  },
  {
    name: 'AI Training',
    description: 'Enables AI model training on account data',
    value: FlagType.DISABLE_AIMI_TRAINING,
    dependsOn: FlagType.ENABLE_AI_AGENTS,
    dependencyLabel: 'AI Agents',
    dependsOnPositiveFlag: true,
  },
  {
    name: 'AI Bring Your Own Model',
    description: 'Enables bringing your own AI model',
    value: FlagType.ENABLE_AI_BYOM,
    positiveFlag: true,
    comingSoon: true,
  },
];

const collectDependents = (
  flagValue: FlagType
): (typeof chariotModuleOptions)[number][] => {
  const directDependents = chariotModuleOptions.filter(
    opt => opt.dependsOn === flagValue
  );
  return directDependents.flatMap(dep => [
    dep,
    ...collectDependents(dep.value),
  ]);
};

export const ChariotModules = ({
  isPraetorianUser = false,
  customerEmail,
  onClose,
  onDeleteAccount,
}: {
  isPraetorianUser: boolean;
  customerEmail?: string;
  onClose?: () => void;
  onDeleteAccount?: () => void;
}) => {
  const { data: flagsData, isLoading: isLoadingFlags } = useMy(
    {
      resource: 'flag',
    },
    { email: customerEmail }
  );
  const { mutateAsync: updateFlags } = useUpdateFlags({
    email: customerEmail,
  });

  /**
   * State management strategy using React 19's useOptimistic hook:
   * - serverFlags: Derived from API data (single source of truth)
   * - optimisticFlags: Managed by useOptimistic for instant UI updates
   *
   * Benefits of useOptimistic:
   * - Automatic rollback on errors (no manual state management)
   * - Simpler mental model (React handles state transitions)
   * - No manual reset logic needed (React syncs with serverFlags)
   * - Type-safe and battle-tested by React team
   *
   * This replaces the previous two-state pattern (serverFlags + localFlags)
   * which required manual synchronization and error handling.
   */

  // Derive server flags from API data - single source of truth
  const serverFlags = useMemo(
    () =>
      (flagsData || [])
        .map(flag => flag.name)
        .filter(name => ChariotModuleFlags.includes(name as FlagType)),
    [flagsData]
  );

  // Use React 19's useOptimistic for automatic optimistic updates and error rollback
  const [optimisticFlags, setOptimisticFlags] = useOptimistic(
    serverFlags,
    (_currentState, optimisticValue: string[]) => optimisticValue
  );

  // Wrapper to match Dispatch<SetStateAction<string[]>> type expected by child components
  const handleSetFlags = (value: SetStateAction<string[]>) => {
    if (typeof value === 'function') {
      setOptimisticFlags(value(optimisticFlags));
    } else {
      setOptimisticFlags(value);
    }
  };

  return (
    <>
      <div className={cn('mb-3 flex flex-col gap-6')}>
        {/* Section Header */}
        <div>
          <h2 className="text-chariot-text-primary mb-1 flex items-center gap-2 text-2xl font-bold">
            <img src="/icons/logo.png" alt="logo" className="size-5" />
            Praetorian Only Section
          </h2>
          <p className="text-chariot-text-secondary text-sm">
            Configure advanced features and settings for this account.
          </p>
        </div>

        {/* Main Container */}
        <div className="border-chariot-stroke bg-chariot-background-primary rounded-lg border">
          <PraetorianSettingsContent
            customerEmail={customerEmail}
            isPraetorianUser={isPraetorianUser}
            isLoadingFlags={isLoadingFlags}
            flags={optimisticFlags}
            setFlags={handleSetFlags}
            updateFlags={updateFlags}
            prevFlags={serverFlags}
            onDeleteAccount={onDeleteAccount}
            showDeleteAccount={true}
            showPlexTrac={!!customerEmail}
          />
        </div>

        {customerEmail && (
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={async () => {
                onClose?.();
              }}
              data-testid="chariot-modules-close-button"
            >
              Close
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export const ChariotModulesOptions = ({
  isLoadingFlags,
  flags,
  setFlags,
  readOnly,
  className,
  checkboxClassName: _checkboxClassName,
  updateFlags,
  prevFlags: _prevFlags,
  disabledFlags = [],
}: {
  isLoadingFlags: boolean;
  flags: string[];
  setFlags?: Dispatch<SetStateAction<string[]>>;
  readOnly?: boolean;
  className?: string;
  checkboxClassName?: string;
  updateFlags?: (params: {
    flagsToAdd?: string[];
    flagsToDelete?: string[];
    successMessage?: string;
    silent?: boolean;
  }) => Promise<unknown>;
  prevFlags?: string[];
  disabledFlags?: string[];
}) => {
  const [updatingFlag, setUpdatingFlag] = useState<string | null>(null);

  const handleToggle = async (
    checked: boolean,
    value: string,
    name: string,
    positiveFlag?: boolean
  ) => {
    if (readOnly || !updateFlags || disabledFlags.includes(value)) {
      return;
    }

    setUpdatingFlag(value);

    try {
      const newFlags = checked
        ? positiveFlag
          ? [...flags, value]
          : flags.filter(flag => flag !== value)
        : positiveFlag
          ? flags.filter(flag => flag !== value)
          : [...flags, value];

      // When disabling, cascade-disable all dependent features
      const dependentsToDisable = !checked
        ? collectDependents(value as FlagType)
        : [];
      for (const dep of dependentsToDisable) {
        if (dep.positiveFlag) {
          const idx = newFlags.indexOf(dep.value);
          if (idx !== -1) newFlags.splice(idx, 1);
        } else {
          if (!newFlags.includes(dep.value)) newFlags.push(dep.value);
        }
      }

      // Optimistic update - useOptimistic will auto-rollback on error
      setFlags?.(newFlags);

      const flagsToAdd = positiveFlag
        ? checked
          ? [value]
          : []
        : !checked
          ? [value]
          : [];
      const flagsToDelete = positiveFlag
        ? checked
          ? []
          : [value]
        : checked
          ? [value]
          : [];

      if (value === FlagType.DISABLE_STATIC_IP_ADDRESSES) {
        if (checked) {
          flagsToAdd.push(FlagType.STATIC);
        } else {
          flagsToDelete.push(FlagType.STATIC);
        }
      }

      for (const dep of dependentsToDisable) {
        if (dep.positiveFlag) {
          if (flags.includes(dep.value) && !flagsToDelete.includes(dep.value)) {
            flagsToDelete.push(dep.value);
          }
        } else {
          if (!flags.includes(dep.value) && !flagsToAdd.includes(dep.value)) {
            flagsToAdd.push(dep.value);
          }
        }
      }

      await updateFlags({
        flagsToAdd,
        flagsToDelete,
        silent: true, // ✅ Suppress hook toast for custom message
      });

      toast.success(`${name} has been ${checked ? 'enabled' : 'disabled'}`);
    } catch (error) {
      // useOptimistic automatically rolls back to serverFlags on error
      // No manual rollback needed!
      toast.error(`Failed to update ${name}. Please try again.`);
      console.error('Failed to update flag:', error);
    } finally {
      setUpdatingFlag(null);
    }
  };

  const isDependencyMet = (
    option: (typeof chariotModuleOptions)[number]
  ): boolean => {
    if (!option.dependsOn) return true;
    const depEnabled = option.dependsOnPositiveFlag
      ? flags.includes(option.dependsOn)
      : !flags.includes(option.dependsOn);
    if (!depEnabled) return false;
    const parentOption = chariotModuleOptions.find(
      o => o.value === option.dependsOn
    );
    if (parentOption) return isDependencyMet(parentOption);
    return true;
  };

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {chariotModuleOptions.map(option => {
        const {
          name,
          description,
          value,
          positiveFlag,
          dependencyLabel,
          comingSoon,
        } = option;
        const isUpdating = updatingFlag === value;
        const isEnabled = positiveFlag
          ? flags.includes(value)
          : !flags.includes(value);
        const isDisabled = disabledFlags.includes(value);
        const depMet = isDependencyMet(option);

        if (comingSoon) {
          return (
            <div
              key={value}
              className={cn(
                'border-chariot-stroke rounded-md border p-4 opacity-60'
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex flex-col gap-1">
                  <h3 className="text-chariot-text-secondary text-base font-bold">
                    {name}
                  </h3>
                  <p className="text-chariot-text-secondary text-sm">
                    {description}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-chariot-text-secondary text-xs font-medium">
                    Coming Soon
                  </span>
                  <ToggleSwitch
                    id={`feature-${value}`}
                    checked={false}
                    disabled={true}
                    onChange={noop}
                  />
                </div>
              </div>
            </div>
          );
        }

        if (!depMet) {
          return (
            <div
              key={value}
              className={cn(
                'border-chariot-stroke rounded-md border p-4 opacity-60'
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex flex-col gap-1">
                  <h3 className="text-chariot-text-secondary text-base font-bold">
                    {name}
                  </h3>
                  <p className="text-chariot-text-secondary text-sm">
                    {description}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Tooltip
                    title={`Enable ${dependencyLabel} first`}
                    placement="top"
                  >
                    <span>
                      <ToggleSwitch
                        id={`feature-${value}`}
                        checked={isEnabled}
                        disabled={true}
                        onChange={noop}
                      />
                    </span>
                  </Tooltip>
                </div>
              </div>
            </div>
          );
        }

        return (
          <div
            key={value}
            className={cn(
              'border-chariot-stroke rounded-md border p-4',
              (readOnly || isDisabled) && 'opacity-60'
            )}
          >
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-1">
                <h3
                  className={cn(
                    'text-chariot-text-primary text-base font-bold',
                    (readOnly || isDisabled) && 'text-chariot-text-secondary'
                  )}
                >
                  {name}
                </h3>
                <p className="text-chariot-text-secondary text-sm">
                  {description}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <ToggleSwitch
                  id={`feature-${value}`}
                  checked={isEnabled}
                  disabled={
                    isLoadingFlags || readOnly || isUpdating || isDisabled
                  }
                  onChange={checked =>
                    handleToggle(checked, value, name, positiveFlag)
                  }
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
